import { AuditLogRecord, AuditLogDiffNode, AuditLogDiffLeaf, NewAuditLogRecord } from './AuditLogPanel.types';
import type { AuditLogActionConfig, LegacyAuditLogRecord } from './AuditLogPanel.types';

export function resolveActorName(
	changedBy: string,
	users: Record<string, { name: string }>,
	systemLabel: string
): string {
	if (changedBy === 'SYSTEM' || changedBy === 'notProvided') return systemLabel;
	if (changedBy === 'FLEXPOS') return 'FlexPOS';
	if (changedBy === 'CUSTOMER') return 'Kunde';
	return users[changedBy]?.name ?? changedBy;
}

const NEW_FORMAT_TYPES = new Set(['created', 'updated', 'deleted']);

export function isNewFormat(record: AuditLogRecord): record is NewAuditLogRecord {
	return 'type' in record && NEW_FORMAT_TYPES.has((record as any).type);
}

export function isCreated(record: AuditLogRecord): boolean {
	if (isNewFormat(record)) return record.type === 'created';
	return Object.keys(record.before).length === 0;
}

export function isDeleted(record: AuditLogRecord): boolean {
	if (isNewFormat(record)) return record.type === 'deleted';
	return !record.after || Object.keys(record.after).length === 0;
}

/** Returns true if the given top-level field changed in this record (any direction). */
export function hasFieldChanged(record: AuditLogRecord, field: string): boolean {
	if (isNewFormat(record)) {
		if (record.type !== 'updated') return false;
		return field in record.diff;
	}
	const v = record.changes[field];
	return v === 'changed' || v === 'added' || v === 'removed';
}

function isDiffLeaf(node: AuditLogDiffNode): node is AuditLogDiffLeaf {
	return 'before' in node && 'after' in node;
}

export interface FlatDiffEntry {
	path: string;
	before: unknown;
	after: unknown;
}

/** Flattens a nested diff tree into a list of { path, before, after } entries. */
export function flattenDiff(
	node: Record<string, AuditLogDiffNode>,
	prefix = ''
): FlatDiffEntry[] {
	const entries: FlatDiffEntry[] = [];
	for (const [key, value] of Object.entries(node)) {
		const path = prefix ? `${prefix} > ${key}` : key;
		if (isDiffLeaf(value)) {
			entries.push({ path, before: value.before, after: value.after });
		} else {
			entries.push(...flattenDiff(value as Record<string, AuditLogDiffNode>, path));
		}
	}
	return entries;
}

/** Find the config entry that matches an action name or prefix. */
export function getActionConfig(
	action: string,
	actions: Record<string, AuditLogActionConfig>
): AuditLogActionConfig | null {
	if (actions[action] !== undefined) return actions[action];
	const prefixMatch = Object.keys(actions).find(key =>
		key.endsWith(':') ? action.startsWith(key) : action.startsWith(key + ':')
	);
	return prefixMatch ? actions[prefixMatch] : null;
}

/**
 * Processes legacy records to detect order-move patterns and filter
 * isProcessingSynchronisation noise. New-format records are passed through unchanged.
 */
export function processRecords(records: AuditLogRecord[]): AuditLogRecord[] {
	const legacyRecords = records.filter(r => !isNewFormat(r)) as LegacyAuditLogRecord[];
	const newFormatRecords = records.filter(r => isNewFormat(r));
	const moveRecord = legacyRecords.find(r => r.changes['customerUid'] === 'removed');

	const cleanLegacy = (list: LegacyAuditLogRecord[]) =>
		list.filter(f => !(f.changes && f.changes['isProcessingSynchronisation']));

	if (moveRecord) {
		const moveRecordTo = legacyRecords.find(
			r => r.changes['customerUid'] === 'added' && r.changedAtRaw >= moveRecord.changedAtRaw
		);
		const remaining = legacyRecords.filter(
			f => Math.abs(moveRecord.changedAtRaw - f.changedAtRaw) >= 2
		);
		if (moveRecordTo?.after?.['customerUid']) {
			const syntheticMove: LegacyAuditLogRecord = {
				...moveRecord,
				action: 'orders-move',
				after: { customerUid: moveRecordTo.after['customerUid'] }
			};
			return [...cleanLegacy(remaining), syntheticMove, ...newFormatRecords];
		}
		return [...cleanLegacy(remaining), ...newFormatRecords];
	}
	return [...cleanLegacy(legacyRecords), ...newFormatRecords];
}

/** Filters out legacy records where only hidden fields changed (would produce empty tables). */
export function filterOnHiddenFields(record: AuditLogRecord, hiddenFields: string[]): boolean {
	if (isNewFormat(record)) return true;
	if (hiddenFields.length === 0) return true;
	const legacy = record as LegacyAuditLogRecord;
	return !hiddenFields.find(d => legacy.changes[d] === 'changed');
}

/** Format a cell value for display. Returns display string and optional full string for tooltip. */
export function formatCellValue(data: unknown): { display: string; full: string | null } {
	if (data === true) return { display: 'true', full: null };
	if (data === false) return { display: 'false', full: null };
	if (data === null || data === undefined) return { display: '\u2014', full: null };

	if (Array.isArray(data)) {
		if (data.length === 0) return { display: '[]', full: null };
		const full = JSON.stringify(data, null, 2);
		if (data.every(v => typeof v !== 'object' || v === null)) {
			const joined = data.join(', ');
			if (joined.length <= 60) return { display: joined, full: null };
			return { display: `${data.length} værdier`, full: joined };
		}
		return { display: `[${data.length} elementer]`, full };
	}

	if (typeof data === 'object') {
		const keys = Object.keys(data as object);
		const full = JSON.stringify(data, null, 2);
		const preview = `{ ${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', \u2026' : ''} }`;
		return { display: preview, full };
	}

	const str = String(data);
	if (str.length > 60) return { display: `${str.substring(0, 57)}\u2026`, full: str };
	return { display: str, full: null };
}
